import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../config/axios';
import { setSentryUser, trackAdminAction } from '../config/sentry';

import { useToast } from '@chakra-ui/react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('temple_token'));
  const toast = useToast();

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/api/auth/me');
          const userData = response.data.user;
          setUser(userData);
          setSentryUser(userData);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('temple_token');
          setToken(null);
          setSentryUser(null);
        }
      } else {
        setSentryUser(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/login', {
        email,
        password
      });

      // Handle Admin 2FA Requirement
      if (response.data.requires2FA) {
        return {
          requires2FA: true,
          tempToken: response.data.tempToken,
          email: response.data.email,
          message: response.data.message
        };
      }

      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('temple_token', newToken);
      setToken(newToken);
      setUser(userData);
      setSentryUser(userData);
      if (['admin', 'super_admin', 'sub_admin'].includes(userData?.role)) {
        trackAdminAction('Admin Login', { email: userData.email, role: userData.role });
      }

      toast({
        title: 'Login successful',
        description: `Welcome back, ${userData.name}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      toast({
        title: error.response?.status === 423 ? 'Account Locked' : 'Login failed',
        description: message,
        status: error.response?.status === 423 ? 'warning' : 'error',
        duration: 5000,
        isClosable: true,
      });
      return {
        success: false,
        error: message,
        locked: error.response?.data?.locked || false,
        remainingMinutes: error.response?.data?.remainingMinutes
      };
    } finally {
      setLoading(false);
    }
  };

  // Verify 2FA OTP
  const verify2FA = async (tempToken, otp) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/verify-2fa', { tempToken, otp });
      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('temple_token', newToken);
      setToken(newToken);
      setUser(userData);

      toast({
        title: 'Admin Verification Successful',
        description: `Welcome back, ${userData.name}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || 'Verification failed';
      toast({
        title: 'Verification Failed',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Resend 2FA OTP
  const resend2FA = async (tempToken) => {
    try {
      const response = await api.post('/api/auth/resend-2fa', { tempToken });
      toast({
        title: 'New Code Dispatched',
        description: response.data.message || 'A new 6-digit code has been sent to your email.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to resend code';
      toast({
        title: 'Resend Failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    }
  };

  // Revoke all sessions across all devices
  const revokeAllSessions = async () => {
    try {
      await api.post('/api/auth/revoke-all-sessions');
      logout();
      toast({
        title: 'Sessions Terminated',
        description: 'All active sessions on all devices have been signed out.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to revoke sessions' };
    }
  };

  // Google Login function
  const googleLogin = async (googleToken) => {
    try {
      setLoading(true);
      const response = await api.post('/api/auth/google', { token: googleToken });

      const { token: newToken, user: userData } = response.data;

      localStorage.setItem('temple_token', newToken);
      setToken(newToken);
      setUser(userData);

      toast({
        title: '',
        description: `Welcome, ${userData.name}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || 'Google Login failed';
      toast({
        title: 'Google auth failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Signup function
  const signup = async (userData) => {
    try {
      setLoading(true);
      // Remove confirmPassword before sending to backend
      const { confirmPassword, ...signupData } = userData;
      const response = await api.post('/api/auth/signup', signupData);

      const { token: newToken, user: newUser } = response.data;

      localStorage.setItem('temple_token', newToken);
      setToken(newToken);
      setUser(newUser);

      toast({
        title: 'Registration successful',
        description: `Welcome to SRI RENUKA ANKAMMA TALLI DEVASTHANAM, ${newUser.name}!`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      let message = 'Registration failed';

      if (error.response?.data) {
        // Handle validation errors
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          message = error.response.data.errors.map(e => e.msg || e.message).join(', ');
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else if (error.response.data.message) {
          message = error.response.data.message;
        }
      }

      toast({
        title: 'Registration failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      // Log full error for debugging
      console.error('Signup error:', error.response?.data || error.message);

      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Clear local storage
      localStorage.removeItem('temple_token');
      setToken(null);
      setUser(null);
      setSentryUser(null);

      // Clear axios headers
      delete api.defaults.headers.common['Authorization'];

      toast({
        title: 'Logged out successfully',
        description: 'Jai SRI RENUKA ANKAMMA TALLI DEVASTHANAM',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: 'Logout failed' };
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await api.put('/api/user/profile', profileData);

      setUser(response.data.user);

      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed';
      toast({
        title: 'Update failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      await api.put('/api/user/change-password', {
        currentPassword,
        newPassword
      });

      toast({
        title: 'Password changed',
        description: 'Your password has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed';
      toast({
        title: 'Password change failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await api.post('/api/auth/forgot-password', { email });

      toast({
        title: 'Reset email sent',
        description: 'Check your email for password reset instructions',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send reset email';
      toast({
        title: 'Reset failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      setLoading(true);
      await api.post('/api/auth/reset-password', {
        token,
        password: newPassword
      });

      toast({
        title: 'Password reset successful',
        description: 'You can now login with your new password',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Password reset failed';
      toast({
        title: 'Reset failed',
        description: message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Check if user is admin
  const isAdmin = () => {
    return user && ['admin', 'sub-admin'].includes(user.role);
  };

  // Check if user is super admin
  const isSuperAdmin = () => {
    return user && user.role === 'admin';
  };

  // Update user data
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    loading,
    token,
    login,
    verify2FA,
    resend2FA,
    revokeAllSessions,
    googleLogin,
    signup,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    hasRole,
    isAdmin,
    isSuperAdmin,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
